/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from "react-native";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc, updateDoc, arrayUnion, Timestamp } from "firebase/firestore";
import { db } from "../firebase.config";
import { getCollectionPath } from "../utils/dbPaths";
import { useNewBookingStore, useAuthStore } from "../stores";
import { createChangeTrackingEntry } from "../utils/helperFuns";

const StripeCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const paymentUrl = location.state?.paymentUrl;
  const bookingId = location.state?.bookingId;
  const referrer = location.state?.referrer || "/rental-bookings"; // Default to bookings page

  useEffect(() => {
    const updatePendingBookingWithWaiver = async () => {
      // Get waiver data from Zustand store
      const store = useNewBookingStore.getState();
      const waiverData = store.waiverData;
      const waiverSigned = store.waiverSigned;
      
      if (!bookingId) {
        console.warn("⚠️ No booking ID - cannot update pending booking with waiver");
        return;
      }
      
      if (!waiverSigned || !waiverData) {
        console.log("ℹ️ No waiver data in store - skipping waiver update");
        return;
      }
      
      try {
        console.log("📝 Updating pending booking with waiver data before payment...");
        
        // Get pending booking path
        const pendingBookingsPath = getCollectionPath("pendingBookings");
        const bookingRef = doc(db, pendingBookingsPath, bookingId);
        
        // Get current booking to read existing waiverData array
        const bookingSnap = await getDoc(bookingRef);

        // The waiver now travels with the booking document — Waiver.js embeds it at
        // creation, and the wizard's skip-waiver path saves it before navigating
        // here. Appending the store's copy on top would double every entry, so only
        // fill the gap when the document exists but has no waiver recorded.
        if (
          bookingSnap.exists() &&
          bookingSnap.data().waiverSigned &&
          (bookingSnap.data().waiverData || []).length > 0
        ) {
          console.log("ℹ️ Waiver already recorded on pending booking — skipping update");
          return;
        }

        const existingWaiverData = bookingSnap.exists() ? (bookingSnap.data().waiverData || []) : [];
        
        // Create waiver entry with proper Timestamp conversion
        const waiverEntry = {
          ...waiverData,
          isOriginalBooker: true,
          signedAt: waiverData.signedAt instanceof Date
            ? Timestamp.fromDate(waiverData.signedAt)
            : (waiverData.signedAt || Timestamp.now()),
        };
        
        // Append waiver entry to existing waiverData array (or create new array if empty)
        const updatedWaiverData = [...existingWaiverData, waiverEntry];
        
        // Get user metadata for change tracking (if authenticated)
        const selectedUser = useAuthStore.getState().selectedUser;
        const userMetadata = selectedUser ? {
          userId: selectedUser.id,
          firstName: selectedUser.firstName || "",
          lastName: selectedUser.lastName || "",
        } : null;

        const updateData = {
          waiverSigned: true,
          waiverData: updatedWaiverData, // Update waiverData field (not waivers)
          updatedAt: Timestamp.now(),
        };

        // Add change tracking if user metadata is available
        if (userMetadata) {
          const changeEntry = createChangeTrackingEntry(userMetadata, {
            changeType: "waiver_signed",
            fieldChanged: "waiverData",
            description: `Waiver signed before payment`,
            timestamp: Timestamp.now(),
          });
          
          if (changeEntry) {
            updateData.editedBy = arrayUnion(changeEntry);
          }
        }

        await updateDoc(bookingRef, updateData);
        console.log("✅ Pending booking updated with waiver data");
      } catch (error) {
        console.error("❌ Error updating pending booking with waiver:", error);
        // Continue anyway - payment can still proceed
      }
    };
    
    const handleRedirect = async () => {
      console.log("🔍 StripeCheckout - Location state:", location.state);
      console.log("🔍 StripeCheckout - Payment URL:", paymentUrl);
      console.log("🔍 StripeCheckout - Booking ID:", bookingId);
      console.log("🔍 StripeCheckout - Referrer:", referrer);
      
      // Check if we're coming back from Stripe (user pressed back button)
      // If there's no paymentUrl but we have a stored referrer, redirect there
      const storedReferrer = localStorage.getItem("stripeCheckoutReferrer");
      if (!paymentUrl && storedReferrer) {
        console.log("🔙 Coming back from Stripe - redirecting to stored referrer:", storedReferrer);
        localStorage.removeItem("stripeCheckoutReferrer");
        navigate(storedReferrer);
        return;
      }
      
      if (!paymentUrl) {
        console.error("❌ No payment URL provided - redirecting back to referrer");
        navigate(referrer);
        return;
      }

      // Update pending booking with waiver data before redirecting to payment
      await updatePendingBookingWithWaiver();

      // Store referrer in localStorage so we can use it if user presses back from Stripe
      localStorage.setItem("stripeCheckoutReferrer", referrer);

      // Stripe Checkout cannot run in iframe - redirect to payment URL
      // Use replace() instead of href to avoid adding to history
      // This way, when user hits back from Stripe, they go back to the referrer page
      console.log("✅ Payment URL found - Redirecting to Stripe Checkout...");
      console.log("🔗 URL:", paymentUrl);
      window.location.replace(paymentUrl);
    };
    
    handleRedirect();
  }, [paymentUrl, navigate, location.state, bookingId, referrer]);

  const handleCancel = () => {
    navigate(referrer);
  };

  if (!paymentUrl) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>No payment URL provided</Text>
        <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.button} onPress={handleCancel}>
          <Text style={styles.buttonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0E7C6B" />
        <Text style={styles.loadingText}>Redirecting to payment...</Text>
        <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.cancelButtonLink} onPress={handleCancel}>
          <Text style={styles.cancelButtonLinkText}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    padding: 20,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 20,
  },
  cancelButtonLink: {
    marginTop: 20,
    padding: 8,
  },
  cancelButtonLinkText: {
    color: "#0E7C6B",
    fontSize: 16,
    textDecorationLine: "underline",
  },
  button: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
    margin: 16,
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  errorText: {
    fontSize: 16,
    color: "#C4453D",
    textAlign: "center",
    margin: 20,
  },
});

export default StripeCheckout;

